import type { PracticeBand } from "./practiceBands";
import type {
  PracticeDifficulty,
  PracticeQuestionTemplate,
} from "./practiceQuestionTemplates";
import type { PracticeCurriculumId } from "./practiceCurricula";

const DIFFICULTY_ORDER: PracticeDifficulty[] = ["intro", "core", "challenge"];

export const MASTERY_WINDOW = 8;
export const MASTERY_BAR = 0.8;
export const MASTERY_MIN_ATTEMPTS = 5;
/**
 * Version 2 keys `skills` by stable skill id. Version 1 keyed them by raw tags
 * and is discarded on read so the two schemes are not mixed.
 */
export const PRACTICE_MASTERY_VERSION = 2 as const;

export type PracticeSkillRecord = {
  attempts: number;
  correct: number;
  lastCorrect: boolean | null;
  recent: boolean[];
  /** Consecutive incorrect checks. Phase 3 steps difficulty down from this. */
  failStreak: number;
  /** Consecutive correct checks. Phase 3 steps difficulty up from this. */
  successStreak: number;
  /** Difficulty of the last exercise recorded for this skill. */
  difficulty: PracticeDifficulty;
  /** Skill to return to after three successes, set when practice stepped down. */
  resumeSkillId?: string;
};

/**
 * One row of `practiceSkillState`.
 * Guests keep this in localStorage. Signed-in students also store it in Postgres.
 */
export type PracticeSkillStateRow = {
  studentKey: string;
  skillId: string;
  difficulty: PracticeDifficulty;
  failStreak: number;
  successStreak: number;
  recent: boolean[];
  attempts: number;
  correct: number;
};

export type PracticeSubjectPathRecord = {
  band: PracticeBand;
  subject: string;
  recent: boolean[];
  pathActive: boolean;
  lastItemId: string | null;
  seenTemplateIds: string[];
};

export type PracticeMasteryState = {
  version: typeof PRACTICE_MASTERY_VERSION;
  curriculumId: PracticeCurriculumId;
  skills: Record<string, PracticeSkillRecord>;
  subjects: Record<string, PracticeSubjectPathRecord>;
};

export function subjectMasteryKey(band: PracticeBand, subject: string): string {
  return `${band}:${subject}`;
}

export function emptySkillRecord(): PracticeSkillRecord {
  return {
    attempts: 0,
    correct: 0,
    lastCorrect: null,
    recent: [],
    failStreak: 0,
    successStreak: 0,
    difficulty: "core",
  };
}

function coerceDifficulty(value: unknown): PracticeDifficulty {
  return value === "intro" || value === "challenge" ? value : "core";
}

function coerceSkillRecord(raw: unknown): PracticeSkillRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Partial<PracticeSkillRecord>;
  if (typeof row.attempts !== "number" || typeof row.correct !== "number") {
    return null;
  }
  return {
    attempts: row.attempts,
    correct: row.correct,
    lastCorrect: typeof row.lastCorrect === "boolean" ? row.lastCorrect : null,
    recent: Array.isArray(row.recent)
      ? row.recent.filter((item): item is boolean => typeof item === "boolean")
      : [],
    failStreak: typeof row.failStreak === "number" ? row.failStreak : 0,
    successStreak: typeof row.successStreak === "number" ? row.successStreak : 0,
    difficulty: coerceDifficulty(row.difficulty),
    ...(typeof row.resumeSkillId === "string" && row.resumeSkillId.trim()
      ? { resumeSkillId: row.resumeSkillId.trim() }
      : {}),
  };
}

export function emptySubjectPath(
  band: PracticeBand,
  subject: string,
): PracticeSubjectPathRecord {
  return {
    band,
    subject,
    recent: [],
    pathActive: false,
    lastItemId: null,
    seenTemplateIds: [],
  };
}

export function emptyMasteryState(
  curriculumId: PracticeCurriculumId,
): PracticeMasteryState {
  return {
    version: PRACTICE_MASTERY_VERSION,
    curriculumId,
    skills: {},
    subjects: {},
  };
}

function coerceSkills(
  raw: PracticeMasteryState["skills"] | undefined,
): Record<string, PracticeSkillRecord> {
  if (!raw || typeof raw !== "object") return {};
  const skills: Record<string, PracticeSkillRecord> = {};
  for (const [skillId, value] of Object.entries(raw)) {
    const record = coerceSkillRecord(value);
    if (record && skillId.trim()) skills[skillId] = record;
  }
  return skills;
}

function pushRecent(recent: boolean[], correct: boolean): boolean[] {
  return [...recent, correct].slice(-MASTERY_WINDOW);
}

export function rollingPercent(recent: boolean[]): number {
  if (!recent.length) return 0;
  const hits = recent.filter(Boolean).length;
  return Math.round((hits / recent.length) * 100);
}

export function isRollingProficient(recent: boolean[]): boolean {
  if (recent.length < MASTERY_MIN_ATTEMPTS) return false;
  const window = recent.slice(-MASTERY_WINDOW);
  return window.filter(Boolean).length / window.length >= MASTERY_BAR;
}

export function skillAccuracy(record: PracticeSkillRecord): number {
  if (record.attempts <= 0) return 0;
  return record.correct / record.attempts;
}

export function isSkillProficient(record: PracticeSkillRecord): boolean {
  return isRollingProficient(record.recent);
}

export function parseMasteryState(
  raw: unknown,
  fallbackCurriculum: PracticeCurriculumId,
): PracticeMasteryState {
  const empty = emptyMasteryState(fallbackCurriculum);
  if (!raw || typeof raw !== "object") return empty;
  const data = raw as Partial<PracticeMasteryState>;
  if (data.version !== PRACTICE_MASTERY_VERSION) return empty;
  return {
    version: PRACTICE_MASTERY_VERSION,
    curriculumId:
      data.curriculumId === "il" ||
      data.curriculumId === "us" ||
      data.curriculumId === "ca" ||
      data.curriculumId === "uk" ||
      data.curriculumId === "au" ||
      data.curriculumId === "generic"
        ? data.curriculumId
        : fallbackCurriculum,
    skills: coerceSkills(data.skills),
    subjects:
      data.subjects && typeof data.subjects === "object" ? data.subjects : {},
  };
}

export function recordCheck(
  state: PracticeMasteryState,
  input: {
    /** When set, this is the only skill updated. Tags are the legacy fallback. */
    skillId?: string;
    tags: string[];
    band: PracticeBand;
    subject: string;
    correct: boolean;
    itemId: string;
    difficulty?: PracticeDifficulty;
  },
): PracticeMasteryState {
  const skillId = input.skillId?.trim();
  const tags = input.tags.map((tag) => tag.trim()).filter(Boolean);
  const keys = skillId ? [skillId] : tags;
  const skills = { ...state.skills };
  for (const key of keys) {
    const prev = skills[key] ?? emptySkillRecord();
    skills[key] = {
      attempts: prev.attempts + 1,
      correct: prev.correct + (input.correct ? 1 : 0),
      lastCorrect: input.correct,
      recent: pushRecent(prev.recent, input.correct),
      failStreak: input.correct ? 0 : prev.failStreak + 1,
      successStreak: input.correct ? prev.successStreak + 1 : 0,
      difficulty: input.difficulty ?? prev.difficulty,
      ...(prev.resumeSkillId ? { resumeSkillId: prev.resumeSkillId } : {}),
    };
  }
  const key = subjectMasteryKey(input.band, input.subject);
  const prevSubject = state.subjects[key] ?? emptySubjectPath(input.band, input.subject);
  const seen = prevSubject.seenTemplateIds.includes(input.itemId)
    ? prevSubject.seenTemplateIds
    : [...prevSubject.seenTemplateIds, input.itemId];
  return {
    ...state,
    skills,
    subjects: {
      ...state.subjects,
      [key]: {
        ...prevSubject,
        recent: pushRecent(prevSubject.recent, input.correct),
        lastItemId: input.itemId,
        seenTemplateIds: seen,
        pathActive: prevSubject.pathActive,
      },
    },
  };
}

export function startSubjectPath(
  state: PracticeMasteryState,
  band: PracticeBand,
  subject: string,
): PracticeMasteryState {
  const key = subjectMasteryKey(band, subject);
  const prev = state.subjects[key] ?? emptySubjectPath(band, subject);
  return {
    ...state,
    subjects: {
      ...state.subjects,
      [key]: { ...prev, pathActive: true },
    },
  };
}

export function endSubjectPath(
  state: PracticeMasteryState,
  band: PracticeBand,
  subject: string,
): PracticeMasteryState {
  const key = subjectMasteryKey(band, subject);
  const prev = state.subjects[key];
  if (!prev) return state;
  return {
    ...state,
    subjects: {
      ...state.subjects,
      [key]: { ...prev, pathActive: false },
    },
  };
}

export function subjectPercent(
  state: PracticeMasteryState,
  band: PracticeBand,
  subject: string,
): number {
  const rec = state.subjects[subjectMasteryKey(band, subject)];
  return rec ? rollingPercent(rec.recent) : 0;
}

export function isSubjectProficient(
  state: PracticeMasteryState,
  band: PracticeBand,
  subject: string,
): boolean {
  const rec = state.subjects[subjectMasteryKey(band, subject)];
  return rec ? isRollingProficient(rec.recent) : false;
}

/** Rows stored in `practiceSkillState` for a signed-in student. */
export function skillStateRows(
  state: PracticeMasteryState,
  studentKey: string,
): PracticeSkillStateRow[] {
  return Object.entries(state.skills).map(([skillId, record]) => ({
    studentKey,
    skillId,
    difficulty: record.difficulty,
    failStreak: record.failStreak,
    successStreak: record.successStreak,
    recent: record.recent,
    attempts: record.attempts,
    correct: record.correct,
  }));
}

function clampCount(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1000, Math.floor(value)));
}

/** Accept a client progress row and force the authenticated student key. */
export function parseSkillStateRow(
  raw: unknown,
  studentKey: string,
): PracticeSkillStateRow | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Partial<PracticeSkillStateRow>;
  const skillId = typeof row.skillId === "string" ? row.skillId.trim() : "";
  if (!skillId || skillId.length > 120) return null;
  if (typeof row.attempts !== "number" || typeof row.correct !== "number") {
    return null;
  }
  if (!Number.isFinite(row.attempts) || !Number.isFinite(row.correct)) return null;
  const attempts = Math.max(0, Math.floor(row.attempts));
  const correct = Math.min(attempts, Math.max(0, Math.floor(row.correct)));
  const recent = Array.isArray(row.recent)
    ? row.recent
        .filter((item): item is boolean => typeof item === "boolean")
        .slice(-MASTERY_WINDOW)
    : [];
  return {
    studentKey,
    skillId,
    difficulty: coerceDifficulty(row.difficulty),
    failStreak: clampCount(row.failStreak),
    successStreak: clampCount(row.successStreak),
    recent,
    attempts,
    correct,
  };
}

/** Add guest counts onto the signed-in row. Recent outcomes stay the last 8. */
export function mergeSkillRows(
  base: PracticeSkillStateRow,
  incoming: PracticeSkillStateRow,
): PracticeSkillStateRow {
  const attempts = base.attempts + incoming.attempts;
  const correct = Math.min(attempts, base.correct + incoming.correct);
  const preferIncoming = incoming.attempts >= base.attempts;
  return {
    studentKey: base.studentKey,
    skillId: base.skillId,
    difficulty: preferIncoming ? incoming.difficulty : base.difficulty,
    failStreak: preferIncoming ? incoming.failStreak : base.failStreak,
    successStreak: preferIncoming ? incoming.successStreak : base.successStreak,
    recent: [...base.recent, ...incoming.recent].slice(-MASTERY_WINDOW),
    attempts,
    correct,
  };
}

export function skillsFromRows(
  rows: readonly PracticeSkillStateRow[],
): Record<string, PracticeSkillRecord> {
  const skills: Record<string, PracticeSkillRecord> = {};
  for (const row of rows) {
    skills[row.skillId] = {
      attempts: row.attempts,
      correct: row.correct,
      lastCorrect: row.recent.length ? row.recent[row.recent.length - 1] : null,
      recent: row.recent.slice(),
      failStreak: row.failStreak,
      successStreak: row.successStreak,
      difficulty: row.difficulty,
    };
  }
  return skills;
}

export function subjectAttemptCount(
  state: PracticeMasteryState,
  band: PracticeBand,
  subject: string,
): number {
  return state.subjects[subjectMasteryKey(band, subject)]?.recent.length ?? 0;
}

/** Weakest tag below the bar, then unseen tags, then lowest accuracy. */
export function pickNextSkill(
  state: PracticeMasteryState,
  availableTags: string[],
): string | null {
  const tags = [...new Set(availableTags.map((tag) => tag.trim()).filter(Boolean))];
  if (!tags.length) return null;

  const scored = tags.map((tag) => {
    const rec = state.skills[tag] ?? emptySkillRecord();
    return { tag, rec };
  });

  const weak = scored
    .filter((row) => row.rec.attempts > 0 && !isSkillProficient(row.rec))
    .sort((a, b) => {
      const acc = skillAccuracy(a.rec) - skillAccuracy(b.rec);
      if (acc !== 0) return acc;
      return b.rec.attempts - a.rec.attempts;
    });
  if (weak[0]) return weak[0].tag;

  const unseen = scored.filter((row) => row.rec.attempts === 0);
  if (unseen[0]) return unseen[0].tag;

  return scored.sort((a, b) => skillAccuracy(a.rec) - skillAccuracy(b.rec))[0]
    ?.tag ?? null;
}

function difficultyRank(difficulty: PracticeDifficulty | undefined): number {
  const idx = DIFFICULTY_ORDER.indexOf(difficulty ?? "core");
  return idx < 0 ? 99 : idx;
}

export function pickNextTemplate(
  templates: readonly PracticeQuestionTemplate[],
  opts: {
    band: PracticeBand;
    subject: string;
    skill: string | null;
    seenIds: string[];
    excludeId?: string;
    grade?: number | null;
    difficulty?: PracticeDifficulty | null;
  },
): PracticeQuestionTemplate | null {
  const pool = templates.filter((template) => {
    if (template.band !== opts.band) return false;
    if (template.subject !== opts.subject) return false;
    if (template.uuid === opts.excludeId) return false;
    if (opts.grade != null) {
      if (opts.grade < template.gradeMin || opts.grade > template.gradeMax) {
        return false;
      }
    }
    if (
      opts.skill &&
      template.skillId !== opts.skill &&
      !template.tags?.includes(opts.skill)
    ) {
      return false;
    }
    return true;
  });
  if (!pool.length) return null;

  const unseen = new Set(
    pool.filter((template) => !opts.seenIds.includes(template.uuid)).map((t) => t.uuid),
  );

  const rank = (template: PracticeQuestionTemplate): [number, number, number] => {
    const diffHit =
      opts.difficulty && template.difficulty !== opts.difficulty ? 1 : 0;
    const seenHit = unseen.has(template.uuid) ? 0 : 1;
    return [diffHit, seenHit, difficultyRank(template.difficulty)];
  };

  return [...pool].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    for (let i = 0; i < ra.length; i += 1) {
      if (ra[i] !== rb[i]) return ra[i] - rb[i];
    }
    return a.id.localeCompare(b.id);
  })[0] ?? null;
}
